import { useSelector } from "react-redux";

import {
  removeStartFromUnderscore,
  stringArrayToNumberArray
} from "@/helpers/string";
import { convertTimeRange } from "@/helpers/time";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { EAdminAreaLevel } from "@/types/adminArea";
import {
  Dataset,
  DatasetDetailSchemaResponse,
  DatasetFilter,
  EFilterOptionType
} from "@/types/dataset";
import {
  AnalysisOutput,
  AreaInput,
  Catchment,
  DatasetConfigAttributeResponse,
  DatasetConfigResponse
} from "@/types/project";
import {
  AdminAreaSettings,
  AreaInputType,
  ECatchmentInputBy,
  ECatchmentTransport,
  ECatchmentType,
  EOutputAnalysisType,
  EOutputGridType,
  ERoadAccessMeasurement,
  ScoringOptionsEnum
} from "@/types/spatial";
import { iterateRows } from "@/utils/catchment";

type MappedFilter = {
  filterType: EFilterOptionType;
  filter: {
    criteria: string;
    include_nulls: boolean;
  } | null;
  attributeId: string;
  attributeCode: string | undefined;
};

const DEFAULT_H3_CONFIG = {
  aggregation_type: "AVG",
  level: 8 // Default H3 resolution level
};

const useConfigurationMappers = () => {
  const { isAdminTreeEnabled } = useAppFeatures();
  const {
    datasets: currentDatasets,
    filters: datasetFilters,
    visualizationOrder: currentVisualizationOrder,
    weightValues,
    history
  } = useSelector((state: RootState) => state.dataset);

  const {
    adminAreaSettings,
    adminTreeSettings,
    catchmentSettings,
    spatialConfig,
    polygonSettings,
    overrideGlobalFilter
  } = useSelector((state: RootState) => state.spatial);
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const { adminAreaSettings: globalAdminAreaSettings, linkedDatasetIds } =
    useSelector((state: RootState) => state.globalFilter);

  /* Check if the analysis result popup is open, will get the datasets from history
    Reference: file useAnalysisResult.tsx line 49
  */
  const datasets = showAnalysisResultPopup ? history.datasets : currentDatasets;
  const visualizationOrder = showAnalysisResultPopup
    ? history.visualizationOrder
    : currentVisualizationOrder;

  const getMaxLevelAdminArea = (obj: AdminAreaSettings | null) => {
    if (!obj) return null;
    let maxLevel: keyof AdminAreaSettings | null = null;

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const typedKey = key as keyof AdminAreaSettings;
        if (obj[typedKey] !== undefined && obj[typedKey] !== null) {
          maxLevel = typedKey;
        }
      }
    }

    return maxLevel;
  };

  const mapCatchmentAreaInput = (): AreaInput | null => {
    if (!catchmentSettings) return null;
    const {
      catchmentType,
      roadAccessConfig,
      radiusData = [],
      placeCoordinates
    } = catchmentSettings;
    const byRadius = catchmentType === ECatchmentType.BY_RADIUS;
    const isDistance =
      !byRadius &&
      roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE;

    const inputByLocation =
      catchmentSettings.catchmentInputBy === ECatchmentInputBy.BY_LOCATION;
    const inputByDataset =
      catchmentSettings.catchmentInputBy === ECatchmentInputBy.BY_DATASET;
    const hasCoordinates = placeCoordinates && placeCoordinates.length > 0;
    const catchmentByDataset = Object.values(
      catchmentSettings.datasetConfig?.datasets ?? {}
    );
    const isochroneData = roadAccessConfig?.isochroneData;

    let locations: Catchment["locations"] = undefined;

    if (inputByLocation && hasCoordinates) {
      locations = placeCoordinates.map((place, index) => ({
        name: place.locationName ?? "",
        coordinates: {
          latitude: place.lat,
          longitude: place.lng
        },
        catchment_area: isochroneData?.[index]?.data ?? null // send isochrone data instead of null, because on API analysis/:id/config does not return isochrone data
      }));
    } else if (inputByDataset && !byRadius && catchmentByDataset) {
      const locationResults: Catchment["locations"] = [];
      iterateRows(
        Object.values(catchmentByDataset),
        (long, lat, label, index) => {
          locationResults.push({
            name: label ?? "",
            coordinates: {
              latitude: lat,
              longitude: long
            },
            catchment_area: isochroneData?.[index]?.data ?? null // send isochrone data instead of null, because on API analysis/:id/config does not return isochrone data
          });
        }
      );
      locations = locationResults;
    }

    return {
      adm_area_id: undefined,
      catchment: {
        profile:
          (byRadius
            ? ECatchmentType.BY_RADIUS
            : roadAccessConfig?.routingType) ?? ECatchmentTransport.WALKING,
        exclusions: byRadius
          ? ([] as string[])
          : (roadAccessConfig?.roadExclusions as string[]),
        metric: byRadius
          ? "distance"
          : (roadAccessConfig?.measurementType ?? ""),
        contours: byRadius
          ? stringArrayToNumberArray(radiusData)
          : isDistance
            ? stringArrayToNumberArray(roadAccessConfig.distances)
            : stringArrayToNumberArray(roadAccessConfig?.durations),
        locations,
        dataset:
          inputByDataset && catchmentByDataset
            ? {
                id: catchmentByDataset[0]?.rawDataset.dataset_id,
                name: catchmentByDataset[0]?.rawDataset.name,
                display_name: catchmentByDataset[0]?.dataset.data.display_name,
                spatial_aggregation_type:
                  catchmentByDataset[0]?.rawDataset.spatial_aggregation_type,
                attributes: processCatchmentDatasetAttributes(
                  catchmentByDataset[0]?.dataset,
                  catchmentByDataset[0]?.filters
                ),
                color_based:
                  catchmentByDataset[0]?.dataset.selectedCategory ?? undefined,
                label_config: {
                  ...catchmentByDataset[0]?.dataset.style.labelConfig,
                  enable_ranking:
                    catchmentByDataset[0]?.dataset.style.rankConfig
                      ?.isEnabled ?? false,
                  max_ranking:
                    catchmentByDataset[0]?.dataset.style.rankConfig
                      ?.maxRanking ?? 10
                }
              }
            : undefined
      }
    };
  };

  const mapAdminAreaInput = (): AreaInput | null => {
    // When admin tree is enabled, use the first explicitly selected area ID
    if (
      isAdminTreeEnabled &&
      adminTreeSettings?.explicitSelectedAreaIds?.length > 0
    ) {
      return { adm_area_id: adminTreeSettings.explicitSelectedAreaIds[0] };
    }

    // Fallback to dropdown-based selection
    const maxLevel = getMaxLevelAdminArea(adminAreaSettings);
    if (!maxLevel) return null;
    return {
      adm_area_id: adminAreaSettings?.[maxLevel]?.value,
      is_global_admin_area: !overrideGlobalFilter
    };
  };

  const mapCustomCatchmentInput = (): AreaInput | null => {
    if (Object.keys(polygonSettings).length === 0) return null;

    return {
      custom_catchments: {
        type: "FeatureCollection",
        features: Object.values(polygonSettings).map((polygon) => ({
          ...polygon.polygon
        }))
      }
    };
  };

  const mappingAreaInput = (): AreaInput => {
    switch (spatialConfig.areaInput?.value) {
      case AreaInputType.CATCHMENT:
        return mapCatchmentAreaInput() ?? {};
      case AreaInputType.POLYGON:
        return mapCustomCatchmentInput() ?? {};
      default:
        return mapAdminAreaInput() ?? {};
    }
  };

  const mappingAnalysisOutput = (): AnalysisOutput | undefined => {
    if (!spatialConfig.outputType) {
      return {
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };
    }

    if (spatialConfig.outputType === EOutputAnalysisType.PROFILING)
      return {
        type: EOutputAnalysisType.PROFILING,
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };

    if (!spatialConfig.gridType) {
      return {
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };
    }

    const geohashType = spatialConfig.gridType === EOutputGridType.GEOHASH;
    const gridLevel = Number(
      geohashType
        ? (spatialConfig.gridResolution?.value ?? 0)
        : (spatialConfig.h3Resolution?.value ?? 0)
    );
    return {
      type: spatialConfig.gridType ?? "",
      grid_level: gridLevel,
      scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
    };
  };

  const mapDatasetFilters = (filter: DatasetFilter): MappedFilter => {
    let criteriaAndNulls = null;

    if (filter.filterType === EFilterOptionType.DATE_TIME) {
      criteriaAndNulls = {
        criteria:
          convertTimeRange(filter.dateTimeRange, filter.time_format) ?? "",
        include_nulls: filter.dateTimeRange?.includeNulls ?? false
      };
    } else if (filter.filterType === EFilterOptionType.MIN_MAX) {
      criteriaAndNulls = {
        criteria: `${filter.minMaxRange?.min}-${filter.minMaxRange?.max}`,
        include_nulls: filter.minMaxRange?.includeNulls ?? false
      };
    } else if (filter.filterType === EFilterOptionType.MULTIPLE) {
      const isAdminAreaFilter = Object.values(EAdminAreaLevel).includes(
        filter.attributeCode as EAdminAreaLevel
      );
      criteriaAndNulls = {
        criteria: isAdminAreaFilter
          ? (filter.admAreaAllSelected?.join("|") ?? "")
          : (filter.selectedOptions?.join("|") ?? ""),
        include_nulls: false
      };
    } else if (filter.filterType === EFilterOptionType.DATE) {
      criteriaAndNulls = {
        criteria: `${filter.dateRange?.from || null}/${filter.dateRange?.to || null}`,
        include_nulls: filter.dateRange?.includeNulls ?? false
      };
    } else if (filter.filterType === EFilterOptionType.TIME) {
      criteriaAndNulls = {
        criteria: `${filter.timeRange?.from || null}/${filter.timeRange?.to || null}`,
        include_nulls: filter.timeRange?.includeNulls ?? false
      };
    }

    return {
      filterType: filter.filterType,
      filter: criteriaAndNulls,
      attributeId: filter.paired_key_attribute_id ?? filter.attributeId,
      attributeCode: filter.attributeCode
    };
  };

  // Filter datasets by acceptable spatial types
  const filterDatasetsByType = () => {
    return Object.entries(datasets).filter(([, dataset]) =>
      [
        "poi",
        "polygon",
        "adm_area",
        EOutputAnalysisType.H3,
        EOutputAnalysisType.GEOHASH
      ].includes(dataset.properties.spatialType)
    );
  };

  const processDatasetAttributes = (
    datasetId: string
  ): DatasetConfigAttributeResponse[] => {
    const filtersArray = datasetFilters[datasetId] ?? [];
    const weightScore = weightValues[datasetId] ?? {};
    const filters: MappedFilter[] = filtersArray.map((filter) =>
      mapDatasetFilters(filter)
    );
    const currDataset = datasets[datasetId];
    const attributeSource =
      currDataset.properties.spatialType === "H3" &&
      currDataset.data.source === "bvt"
        ? currDataset.data.schema
        : currDataset.categories;

    // Track matched filters to prevent the same filter from being matched to multiple attributes
    // This can happen when one attribute's id equals another's paired_key_attribute_id
    // Note: Admin area filters are excluded from this deduplication because they intentionally
    // match multiple schema items (adm_area_level_1, adm_area_level_2, etc.) and get deduplicated later
    const matchedFilterKeys = new Set<string>();

    return (
      attributeSource?.map((attribute) => {
        const attributeId = attribute.paired_key_attribute_id ?? attribute.id;
        let matchedIsAdmArea = false;

        const matchedFilter = filters.find((f) => {
          const isAdmAreaFilter = f.filter?.criteria?.includes("::");

          if (isAdmAreaFilter) {
            // Admin area filter has ::. For admin area filters, only check attribute id
            if (f.attributeId === attributeId) {
              matchedIsAdmArea = true;
              return true;
            }
          } else {
            // For non-admin-area filters, skip if already matched to prevent duplication
            const filterKey = `${f.attributeId}_${f.attributeCode ?? ""}`;
            if (matchedFilterKeys.has(filterKey)) return false;

            if (
              f.attributeId === attributeId &&
              f.attributeCode === attribute.attribute_code
            ) {
              matchedIsAdmArea = false;
              return true;
            }
          }
          return false;
        }) || { filter: null, filterType: null };

        // Mark non-admin-area filters as matched to prevent duplication
        if (matchedFilter.filter && !matchedIsAdmArea) {
          const filterKey = `${(matchedFilter as MappedFilter).attributeId}_${(matchedFilter as MappedFilter).attributeCode}`;
          matchedFilterKeys.add(filterKey);
        }

        const { filter, filterType } = matchedFilter;

        return {
          filter,
          id: attributeId,
          weight_score: Number(
            weightScore?.attributes?.[attributeId]?.weight ?? 0
          ),
          ...(weightScore?.attributes?.[attributeId]?.negative_impact
            ? {
                weight_negative_impact:
                  weightScore?.attributes?.[attributeId]?.negative_impact
              }
            : {}),
          ...(filterType ? { filterType } : {}),
          ...(attribute.attribute_code
            ? { attribute_code: attribute.attribute_code }
            : {})
        };
      }) || []
    );
  };

  const processCatchmentDatasetAttributes = (
    dataset: Dataset,
    filters: DatasetFilter[]
  ): DatasetConfigAttributeResponse[] => {
    const mappedFilters: MappedFilter[] = filters.map((filter) =>
      mapDatasetFilters(filter)
    );

    // Track matched filters to prevent the same filter from being matched to multiple attributes
    const matchedFilterKeys = new Set<string>();

    return (
      dataset.categories?.map((attribute) => {
        const attributeId = attribute.paired_key_attribute_id ?? attribute.id;
        const matchedFilter = mappedFilters.find((f) => {
          const filterKey = `${f.attributeId}_${f.attributeCode}`;
          if (matchedFilterKeys.has(filterKey)) return false;

          const isMatch = f.filter?.criteria?.includes("::")
            ? f.attributeId === attributeId
            : f.attributeId === attributeId &&
              f.attributeCode === attribute.attribute_code;

          return isMatch;
        }) || { filter: null, filterType: null };

        if (matchedFilter.filter) {
          const filterKey = `${(matchedFilter as MappedFilter).attributeId}_${(matchedFilter as MappedFilter).attributeCode}`;
          matchedFilterKeys.add(filterKey);
        }

        const { filter, filterType } = matchedFilter;
        return {
          filter,
          id: attributeId,
          weight_score: null,
          ...(filterType ? { filterType } : {}),
          ...(attribute.attribute_code
            ? { attribute_code: attribute.attribute_code }
            : {})
        };
      }) || []
    );
  };

  // Process create final attributes with admin area consolidation
  const createFinalAttributes = (
    nonAdminAttributes: DatasetConfigAttributeResponse[],
    adminAreaAttributes: Map<string, DatasetConfigAttributeResponse>
  ): DatasetConfigAttributeResponse[] => {
    const finalAttributes = [...nonAdminAttributes];

    if (adminAreaAttributes.size > 0) {
      const sortedLevels = Array.from(adminAreaAttributes.keys()).sort((a, b) =>
        a.localeCompare(b)
      );

      const primaryAdminAttr = adminAreaAttributes.get(sortedLevels[0])!;
      const maxWeightScore = Math.max(
        ...Array.from(adminAreaAttributes.values()).map(
          (attr) => attr.weight_score || 0
        )
      );
      finalAttributes.push({
        ...primaryAdminAttr,
        id: primaryAdminAttr.id,
        weight_score: maxWeightScore
      });
    }

    return finalAttributes;
  };

  // Process separate admin area and non-admin attributes
  const separateAdminAreaAttributes = (
    combinedAttributes: DatasetConfigAttributeResponse[],
    primaryDataset: Dataset
  ) => {
    const adminAreaAttributes = new Map<
      string,
      DatasetConfigAttributeResponse
    >();
    const nonAdminAttributes: DatasetConfigAttributeResponse[] = [];

    const isH3Dataset =
      primaryDataset.properties.spatialType === "H3" &&
      primaryDataset.data.source === "bvt";

    Array.from(combinedAttributes.values()).forEach((attr) => {
      let category: DatasetDetailSchemaResponse | undefined;
      if (isH3Dataset) {
        category = primaryDataset.data.schema.find(
          (item) => (item.paired_key_attribute_id ?? item.id) === attr.id
        );
      } else {
        category = primaryDataset.categories.find(
          (item) => (item.paired_key_attribute_id ?? item.id) === attr.id
        );
      }
      const attributeCode = category?.attribute_code;

      if (attributeCode?.startsWith("adm_area_level_")) {
        adminAreaAttributes.set(attributeCode, attr);
      } else {
        nonAdminAttributes.push(attr);
      }
    });

    return { adminAreaAttributes, nonAdminAttributes };
  };

  const mappingDataset = (): DatasetConfigResponse[] => {
    if (!Object.keys(datasets).length) return [];

    const filteredDatasets = filterDatasetsByType();

    // Optimize lookup by creating a Map for O(1) access
    const datasetMap = new Map(filteredDatasets);

    const orderedFilteredDatasets = visualizationOrder
      .filter((datasetId) => datasetMap.has(datasetId))
      .map(
        (datasetId) =>
          [datasetId, datasetMap.get(datasetId)!] as [string, Dataset]
      );

    return orderedFilteredDatasets.map(([datasetId, dataset]) => {
      const attributes = processDatasetAttributes(datasetId);

      // nonAdminAttributes can have non-unique attribute id
      // Thus, the Set cannot be used to filter them
      const { adminAreaAttributes, nonAdminAttributes } =
        separateAdminAreaAttributes(attributes, dataset);

      const finalAttributes = createFinalAttributes(
        nonAdminAttributes,
        adminAreaAttributes
      );

      return {
        id: removeStartFromUnderscore(datasetId),
        display_name: dataset?.data?.display_name,
        spatial_aggregation_type: dataset.properties.spatialType,
        attributes: finalAttributes,
        color_based: dataset?.selectedCategory,
        color_scale: dataset?.style?.colorFamily,
        opacity: dataset?.style?.fillOpacity,
        grid_config: getGridConfig(dataset),
        color_steps: dataset?.style?.colorSteps,
        label_config: {
          ...dataset?.style?.labelConfig,
          enable_ranking: dataset?.style?.rankConfig?.isEnabled ?? false,
          max_ranking: dataset?.style?.rankConfig?.maxRanking ?? 10
        },
        is_global_admin_area: linkedDatasetIds.includes(datasetId)
      };
    });
  };

  const getGridConfig = (currentDataset: Dataset) => {
    if (currentDataset?.h3Configuration) {
      return {
        aggregation_type:
          currentDataset?.h3Configuration?.aggregationType ?? "AVG",
        level: currentDataset?.h3Configuration?.resolution ?? 8
      };
    }

    return currentDataset?.properties?.spatialType === "H3" &&
      currentDataset?.data?.source === "bvt"
      ? DEFAULT_H3_CONFIG
      : undefined;
  };

  const mappingAdmAreaIds = (): string[] => {
    const maxLevel = getMaxLevelAdminArea(globalAdminAreaSettings);
    if (!maxLevel) return [];

    const value = globalAdminAreaSettings?.[maxLevel]?.value;
    return value ? [value] : [];
  };

  return {
    getGridConfig,
    mappingDataset,
    mappingAreaInput,
    mappingAdmAreaIds,
    mappingAnalysisOutput
  };
};

export default useConfigurationMappers;
